/**
 * ErrorBoundary Component
 * React error boundary for graceful error handling
 */

import React, { Component, ReactNode } from 'react';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="ai360-error-boundary">
          <div className="ai360-error-boundary-content">
            <svg 
              className="ai360-error-boundary-icon" 
              width="48" 
              height="48" 
              viewBox="0 0 48 48" 
              fill="none"
            >
              <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" />
              <path 
                d="M24 16v8m0 8v.5" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
              />
            </svg>
            
            <h3 className="ai360-error-boundary-title">
              Algo salió mal
            </h3>
            
            <p className="ai360-error-boundary-message">
              Lo sentimos, ha ocurrido un error inesperado. 
              Por favor, intenta recargar la página.
            </p>

            {this.state.error && (
              <details className="ai360-error-boundary-details">
                <summary>Detalles del error</summary>
                <pre>{this.state.error.message}</pre>
              </details>
            )}

            <button
              type="button"
              onClick={this.handleReset}
              className="ai360-error-boundary-button"
            >
              Intentar de nuevo
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface ErrorMessageProps {
  title?: string;
  message: string;
  onRetry?: () => void;
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  title = 'Error',
  message,
  onRetry,
}) => {
  return (
    <div className="ai360-error-message">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path 
          d="M12 8v4m0 4v.5" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
        />
      </svg>
      
      <div className="ai360-error-message-content">
        <h4 className="ai360-error-message-title">{title}</h4>
        <p className="ai360-error-message-text">{message}</p>
      </div>

      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="ai360-error-message-retry"
        >
          Reintentar
        </button>
      )}
    </div>
  );
};
